import React, { useState, useEffect } from 'react';
import { useAuth } from '../../components/hooks/useAuth';
import { getAllAIConfigs, updateAIConfig, getCenters } from '../../services/supabaseService';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import Card from '../../components/ui/Card';
import Select from '../../components/ui/Select';
import Input from '../../components/ui/Input';

const AITuning = () => {
  const { userProfile } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [selectedCenter, setSelectedCenter] = useState('');
  const [centers, setCenters] = useState([]);
  const [configs, setConfigs] = useState({
    churnRisk: null,
    vacancyPrediction: null,
    dynamicPricing: null,
  });
  const [simulatorInputs, setSimulatorInputs] = useState({
    occupancyPercent: 75,
    vacantSeats: 20,
    lockInExpiringDays: 60,
  });
  const [simulatorResults, setSimulatorResults] = useState(null);

  useEffect(() => {
    if (userProfile?.role === 'admin') {
      fetchCenters();
    }
  }, [userProfile]);

  useEffect(() => {
    if (selectedCenter) {
      fetchConfigs();
    }
  }, [selectedCenter]);

  const fetchCenters = async () => {
    try {
      const data = await getCenters();
      setCenters(data || []);
      if (data?.length > 0) {
        setSelectedCenter(data?.[0]?.id);
      }
    } catch (error) {
      console.error('Error fetching centers:', error);
    }
  };

  const fetchConfigs = async () => {
    try {
      setLoading(true);
      const data = await getAllAIConfigs(selectedCenter);
      const configMap = {
        churnRisk: data?.find((c) => c?.configType === 'churn_risk'),
        vacancyPrediction: data?.find((c) => c?.configType === 'vacancy_prediction'),
        dynamicPricing: data?.find((c) => c?.configType === 'dynamic_pricing'),
      };
      setConfigs(configMap);
    } catch (error) {
      console.error('Error fetching configs:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleConfigChange = (configType, field, value) => {
    setConfigs((prev) => ({
      ...prev,
      [configType]: {
        ...prev?.[configType],
        [field]: parseFloat(value),
      },
    }));
  };

  const handleSaveConfig = async (configType) => {
    try {
      setSaving(true);
      const config = configs?.[configType];
      if (!config?.id) return;

      await updateAIConfig(config?.id, config);
      alert('AI configuration saved successfully!');
    } catch (error) {
      console.error('Error saving config:', error);
      alert(error?.message || 'Failed to save configuration');
    } finally {
      setSaving(false);
    }
  };

  const handleSimulatorChange = (field, value) => {
    setSimulatorInputs((prev) => ({ ...prev, [field]: parseFloat(value) }));
  };

  const runSimulator = () => {
    const { occupancyPercent, vacantSeats, lockInExpiringDays } = simulatorInputs;
    const churnConfig = configs?.churnRisk;
    const pricingConfig = configs?.dynamicPricing;

    // Simplified churn risk calculation
    const lockInRisk = lockInExpiringDays < 30 ? 0.4 : lockInExpiringDays < 60 ? 0.2 : 0.1;
    const occupancyRisk = occupancyPercent < 70 ? 0.3 : occupancyPercent < 85 ? 0.1 : 0.05;
    const churnRisk = (lockInRisk * (churnConfig?.lockInExpiryWeight || 0.35)) + (occupancyRisk * (churnConfig?.occupancyDropWeight || 0.20));

    // Simplified pricing suggestion
    let pricingAction = 'maintain';
    let pricingChange = 0;
    const drivers = [];

    if (occupancyPercent > (pricingConfig?.maxOccupancyTriggerPercent || 95)) {
      pricingAction = 'increase';
      pricingChange = pricingConfig?.maxPriceIncreasePercent || 15;
      drivers?.push('High occupancy (>95%)');
    } else if (occupancyPercent < (pricingConfig?.minOccupancyTriggerPercent || 70)) {
      pricingAction = 'decrease';
      pricingChange = pricingConfig?.maxPriceDecreasePercent || 10;
      drivers?.push('Low occupancy (<70%)');
    }

    if (vacantSeats > 15) {
      drivers?.push(`High vacancy (${vacantSeats} seats)`);
    }

    if (lockInExpiringDays < 30) {
      drivers?.push('Lock-ins expiring soon');
    }

    setSimulatorResults({
      churnRisk: (churnRisk * 100)?.toFixed(1),
      churnLevel: churnRisk > 0.3 ? 'High' : churnRisk > 0.15 ? 'Medium' : 'Low',
      pricingAction,
      pricingChange,
      drivers: drivers?.length > 0 ? drivers : ['No significant risk factors'],
    });
  };

  if (userProfile?.role !== 'admin') {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <Icon name="Lock" size={48} className="mx-auto mb-4 text-red-500" />
          <h2 className="text-2xl font-bold mb-2">Access Denied</h2>
          <p className="text-gray-600">Only administrators can access AI Tuning.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-2">
            <Icon name="Settings" size={32} />
            AI Tuning
          </h1>
          <p className="text-gray-600 mt-1">Configure AI logic weights and thresholds for predictions and recommendations</p>
        </div>
        <Select
          value={selectedCenter}
          onChange={(e) => setSelectedCenter(e?.target?.value)}
          options={centers?.map((c) => ({ value: c?.id, label: c?.name }))}
          className="w-64"
        />
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      ) : (
        <div className="space-y-6">
          {/* Churn Risk Config */}
          <Card>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-bold text-gray-900 flex items-center gap-2">
                <Icon name="AlertTriangle" size={24} />
                Churn Risk Weights
              </h3>
              <Button onClick={() => handleSaveConfig('churnRisk')} loading={saving} size="sm">
                Save
              </Button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Lock-in Expiry Weight: {configs?.churnRisk?.lockInExpiryWeight?.toFixed(2) || 0.35}
                  <span className="ml-2 text-xs text-gray-500" title="Impact of upcoming lock-in expiry on churn risk">
                    <Icon name="Info" size={14} className="inline" />
                  </span>
                </label>
                <input
                  type="range"
                  min="0"
                  max="1"
                  step="0.05"
                  value={configs?.churnRisk?.lockInExpiryWeight || 0.35}
                  onChange={(e) => handleConfigChange('churnRisk', 'lockInExpiryWeight', e?.target?.value)}
                  className="w-full"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Payment Delay Weight: {configs?.churnRisk?.paymentDelayWeight?.toFixed(2) || 0.25}
                  <span className="ml-2 text-xs text-gray-500" title="Impact of payment delays on churn risk">
                    <Icon name="Info" size={14} className="inline" />
                  </span>
                </label>
                <input
                  type="range"
                  min="0"
                  max="1"
                  step="0.05"
                  value={configs?.churnRisk?.paymentDelayWeight || 0.25}
                  onChange={(e) => handleConfigChange('churnRisk', 'paymentDelayWeight', e?.target?.value)}
                  className="w-full"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Occupancy Drop Weight: {configs?.churnRisk?.occupancyDropWeight?.toFixed(2) || 0.20}
                  <span className="ml-2 text-xs text-gray-500" title="Impact of occupancy decrease on churn risk">
                    <Icon name="Info" size={14} className="inline" />
                  </span>
                </label>
                <input
                  type="range"
                  min="0"
                  max="1"
                  step="0.05"
                  value={configs?.churnRisk?.occupancyDropWeight || 0.20}
                  onChange={(e) => handleConfigChange('churnRisk', 'occupancyDropWeight', e?.target?.value)}
                  className="w-full"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Renewal History Weight: {configs?.churnRisk?.renewalHistoryWeight?.toFixed(2) || 0.20}
                  <span className="ml-2 text-xs text-gray-500" title="Impact of past renewal behavior on churn risk">
                    <Icon name="Info" size={14} className="inline" />
                  </span>
                </label>
                <input
                  type="range"
                  min="0"
                  max="1"
                  step="0.05"
                  value={configs?.churnRisk?.renewalHistoryWeight || 0.20}
                  onChange={(e) => handleConfigChange('churnRisk', 'renewalHistoryWeight', e?.target?.value)}
                  className="w-full"
                />
              </div>
            </div>
          </Card>

          {/* Vacancy Prediction Config */}
          <Card>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-bold text-gray-900 flex items-center gap-2">
                <Icon name="Home" size={24} />
                Vacancy Prediction Thresholds
              </h3>
              <Button onClick={() => handleSaveConfig('vacancyPrediction')} loading={saving} size="sm">
                Save
              </Button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Input
                label="Vacancy Threshold (Seats)"
                type="number"
                value={configs?.vacancyPrediction?.vacancyThresholdSeats || 10}
                onChange={(e) => handleConfigChange('vacancyPrediction', 'vacancyThresholdSeats', e?.target?.value)}
                description="Alert when vacant seats exceed this number"
              />
              <Input
                label="Vacancy Alert Days"
                type="number"
                value={configs?.vacancyPrediction?.vacancyAlertDays || 30}
                onChange={(e) => handleConfigChange('vacancyPrediction', 'vacancyAlertDays', e?.target?.value)}
                description="Days ahead to predict vacancy"
              />
              <Input
                label="Occupancy Target (%)"
                type="number"
                value={configs?.vacancyPrediction?.occupancyTargetPercent || 85}
                onChange={(e) => handleConfigChange('vacancyPrediction', 'occupancyTargetPercent', e?.target?.value)}
                description="Target occupancy percentage"
              />
            </div>
          </Card>

          {/* Dynamic Pricing Config */}
          <Card>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-bold text-gray-900 flex items-center gap-2">
                <Icon name="TrendingUp" size={24} />
                Dynamic Pricing Bounds
              </h3>
              <Button onClick={() => handleSaveConfig('dynamicPricing')} loading={saving} size="sm">
                Save
              </Button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Input
                label="Max Price Increase (%)"
                type="number"
                value={configs?.dynamicPricing?.maxPriceIncreasePercent || 15}
                onChange={(e) => handleConfigChange('dynamicPricing', 'maxPriceIncreasePercent', e?.target?.value)}
                description="Maximum allowed price increase"
              />
              <Input
                label="Max Price Decrease (%)"
                type="number"
                value={configs?.dynamicPricing?.maxPriceDecreasePercent || 10}
                onChange={(e) => handleConfigChange('dynamicPricing', 'maxPriceDecreasePercent', e?.target?.value)}
                description="Maximum allowed price decrease"
              />
              <Input
                label="Min Occupancy Trigger (%)"
                type="number"
                value={configs?.dynamicPricing?.minOccupancyTriggerPercent || 70}
                onChange={(e) => handleConfigChange('dynamicPricing', 'minOccupancyTriggerPercent', e?.target?.value)}
                description="Occupancy below this triggers price decrease"
              />
              <Input
                label="Max Occupancy Trigger (%)"
                type="number"
                value={configs?.dynamicPricing?.maxOccupancyTriggerPercent || 95}
                onChange={(e) => handleConfigChange('dynamicPricing', 'maxOccupancyTriggerPercent', e?.target?.value)}
                description="Occupancy above this triggers price increase"
              />
            </div>
          </Card>

          {/* What-if Simulator */}
          <Card className="bg-gradient-to-br from-blue-50 to-purple-50">
            <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
              <Icon name="Zap" size={24} />
              What-if Simulator
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
              <Input
                label="Occupancy (%)"
                type="number"
                value={simulatorInputs?.occupancyPercent}
                onChange={(e) => handleSimulatorChange('occupancyPercent', e?.target?.value)}
              />
              <Input
                label="Vacant Seats"
                type="number"
                value={simulatorInputs?.vacantSeats}
                onChange={(e) => handleSimulatorChange('vacantSeats', e?.target?.value)}
              />
              <Input
                label="Lock-in Expiring (Days)"
                type="number"
                value={simulatorInputs?.lockInExpiringDays}
                onChange={(e) => handleSimulatorChange('lockInExpiringDays', e?.target?.value)}
              />
            </div>
            <Button onClick={runSimulator} className="mb-4 flex items-center gap-2">
              <Icon name="Play" size={16} />
              Run Simulation
            </Button>

            {simulatorResults && (
              <div className="bg-white rounded-lg p-4 space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                    <h4 className="text-sm font-semibold text-red-900 mb-2">Churn Risk</h4>
                    <p className="text-3xl font-bold text-red-900">{simulatorResults?.churnRisk}%</p>
                    <p className="text-sm text-red-700 mt-1">Risk Level: {simulatorResults?.churnLevel}</p>
                  </div>
                  <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                    <h4 className="text-sm font-semibold text-green-900 mb-2">Pricing Suggestion</h4>
                    <p className="text-3xl font-bold text-green-900 capitalize">{simulatorResults?.pricingAction}</p>
                    {simulatorResults?.pricingChange > 0 && (
                      <p className="text-sm text-green-700 mt-1">Change: {simulatorResults?.pricingChange}%</p>
                    )}
                  </div>
                </div>
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <h4 className="text-sm font-semibold text-blue-900 mb-2">Top Drivers</h4>
                  <ul className="list-disc list-inside text-sm text-blue-700 space-y-1">
                    {simulatorResults?.drivers?.slice(0, 3)?.map((driver, index) => (
                      <li key={index}>{driver}</li>
                    ))}
                  </ul>
                </div>
              </div>
            )}
          </Card>
        </div>
      )}
    </div>
  );
};

export default AITuning;